import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getRadialMenuPositions, type RadialMenuActionKey } from '../interaction/radialMenu';
import type { RenderFurniture } from '../../../domain/types';

const RADIAL_OPTION_MIN_WIDTH = 136; // Мінімальна ширина, кнопка може розширюватися
const RADIAL_OPTION_HEIGHT = 22;
/** The menu opens on a tap; a habitual double tap's second tap must not close it again. */
const IGNORE_BACKDROP_MS = 400;

interface RadialMenuOverlayProps {
  selectedFurniture: RenderFurniture;
  menuOptions: Array<{ key: RadialMenuActionKey; icon: string; textKey: string; adminOnly?: boolean }>;
  menuPosition: { x: number; y: number };
  highlightedRadialAction: RadialMenuActionKey | null;
  setHighlightedRadialAction: (key: RadialMenuActionKey | null) => void;
  closeRadialMenu: () => void;
  executeRadialAction: (key: RadialMenuActionKey, furniture?: RenderFurniture | null) => void;
}

/** Shared radial menu — furniture only (backdrop + fanned-out action buttons). */
export default function RadialMenuOverlay({
  selectedFurniture,
  menuOptions,
  menuPosition,
  highlightedRadialAction,
  setHighlightedRadialAction,
  closeRadialMenu,
  executeRadialAction,
}: RadialMenuOverlayProps) {
  const { t } = useTranslation();
  const positions = getRadialMenuPositions(menuOptions.length);
  const openedAt = useRef(0);
  useEffect(() => { openedAt.current = performance.now(); }, []);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 3000,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
          if (performance.now() - openedAt.current < IGNORE_BACKDROP_MS) return;
          closeRadialMenu();
        }}
      />
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 3001,
          pointerEvents: 'none',
        }}
      >
        {menuOptions.map((item, index) => {
          const position = positions[index];
          if (!position) return null;
          const x = menuPosition.x + position.xOffset;
          const y = menuPosition.y + position.yOffset;
          const isHighlighted = highlightedRadialAction === item.key;

          return (
            <div
              key={item.key}
              data-tutorial={`radial-${item.key}`}
              style={{
                position: 'fixed',
                left: `${x}px`,
                top: `${y}px`,
                transform: 'translate(-50%, -50%)',
                zIndex: 3002,
                pointerEvents: 'auto',
                animation: `fadeInScale 0.2s ease ${index * 0.1}s both`,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  height: '30px',
                }}
              >
                {item.key !== 'buy' && item.key !== 'buy_all' && item.key !== 'unplace' && item.icon && (
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      height: '22px',
                      marginRight: '-11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={item.icon}
                      alt=""
                      style={{
                        height: '22px',
                        width: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </div>
                )}
                {(item.key === 'buy' || item.key === 'buy_all') && (
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      height: '22px',
                      marginRight: '-11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                    }}
                  >
                    💰
                  </div>
                )}
                {item.key === 'unplace' && (
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      height: '22px',
                      marginRight: '-11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                    }}
                  >
                    ❌
                  </div>
                )}
                <button
                  type="button"
                  data-tutorial={`radial-${item.key}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedFurniture) {
                      executeRadialAction(item.key, selectedFurniture);
                      closeRadialMenu();
                    }
                  }}
                  onMouseEnter={() => setHighlightedRadialAction(item.key)}
                  onMouseLeave={() => setHighlightedRadialAction(null)}
                  style={{
                    height: `${RADIAL_OPTION_HEIGHT}px`,
                    minWidth: `${RADIAL_OPTION_MIN_WIDTH}px`,
                    padding: '0 20px 0 20px',
                    background: isHighlighted ? '#eaf3ff' : '#fff',
                    border: '1px solid #d0d0d0',
                    borderRadius: '11px',
                    boxShadow: '0 2px 8px rgba(255,255,255,0.3)',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1e4f9b',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.adminOnly ? item.textKey : t(item.textKey)}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {/* Extract radial menu keys for i18next-parser (static t() calls, never rendered) */}
      {/* eslint-disable-next-line no-constant-binary-expression -- dead branch keeps keys visible to i18next-parser */}
      {false && (
        <>
          {t("Flip")}
          {t("Hide")}
          {t("Buy now")}
          {t("Pay all")}
          {t("All furniture purchased")}
          {t("Not enough pearls to buy all items")}
          {t("Cancel")}
          {t("Failed to save tiles. Please try again.")}
        </>
      )}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
    </>
  );
}
